<template>
  <div class="grey lighten-5" :class="$vuetify.breakpoint.xs ? 'pa-2' : 'pa-6'" style="min-height: 100vh;">
    <v-container style="max-width: 1100px;">

      <!-- Header -->
      <div class="text-center mb-4">
        <h1 :class="$vuetify.breakpoint.xs ? 'text-h5' : 'text-h4'" class="font-weight-bold grey--text text--darken-3">
          <v-icon :large="!$vuetify.breakpoint.xs" color="deep-purple">mdi-calendar-star</v-icon>
          Eventos
        </h1>
        <p class="grey--text caption">Descubre eventos presenciales y virtuales de la región</p>
      </div>

      <!-- Filtros -->
      <v-row class="mb-4" align="center">
        <!-- Categoría: select en mobile, chips en desktop -->
        <v-col cols="12" sm="5">
          <v-select v-if="$vuetify.breakpoint.xs" v-model="selectedCategory"
            :items="categoriasSelect" item-text="text" item-value="value"
            label="Categoría" outlined dense hide-details prepend-inner-icon="mdi-folder"></v-select>
          <v-chip-group v-else v-model="selectedCategory" mandatory active-class="deep-purple white--text">
            <v-chip filter value="all">Todos</v-chip>
            <v-chip v-for="cat in categorias" :key="cat.id" filter :value="cat.slug">
              <v-icon left small v-if="cat.icon">{{ cat.icon }}</v-icon>
              {{ cat.name }}
            </v-chip>
          </v-chip-group>
        </v-col>

        <!-- Temporal -->
        <v-col cols="12" sm="4">
          <v-select v-if="$vuetify.breakpoint.xs" v-model="filterTemporal"
            :items="temporalOptions" item-text="text" item-value="value"
            label="Mostrar" outlined dense hide-details prepend-inner-icon="mdi-filter"></v-select>
          <v-btn-toggle v-else v-model="filterTemporal" mandatory dense color="deep-purple">
            <v-btn value="upcoming" small>Próximos</v-btn>
            <v-btn value="past" small>Pasados</v-btn>
            <v-btn value="all" small>Todos</v-btn>
          </v-btn-toggle>
        </v-col>

        <!-- Tipo -->
        <v-col cols="12" sm="3">
          <v-select v-model="filterType" :items="typeOptions" label="Tipo"
            outlined dense hide-details></v-select>
        </v-col>
      </v-row>

      <!-- Grid de eventos -->
      <v-row v-if="eventos.length">
        <v-col v-for="ev in eventos" :key="ev.id" cols="12" sm="6" md="4" lg="3">
          <v-card hover class="event-card" @click="openDetail(ev)">
            <!-- Imagen -->
            <v-img v-if="ev.image" :src="getImageUrl(ev)" aspect-ratio="1"
              gradient="to bottom, rgba(0,0,0,0), rgba(0,0,0,0.3)">
              <div class="pa-2">
                <v-chip x-small :color="tipoColor(ev.type)" dark>
                  <v-icon x-small left>{{ tipoIcon(ev.type) }}</v-icon>
                  {{ tipoLabel(ev.type) }}
                </v-chip>
              </div>
            </v-img>
            <v-img v-else aspect-ratio="1" class="deep-purple lighten-4 d-flex align-center justify-center">
              <v-icon size="64" color="deep-purple lighten-1">mdi-calendar-star</v-icon>
            </v-img>

            <!-- Contenido -->
            <v-card-text class="pb-1">
              <h3 class="subtitle-1 font-weight-bold text--primary mb-1">{{ ev.title }}</h3>
              <p class="caption grey--text mb-1">
                <v-icon x-small>mdi-calendar</v-icon>
                {{ formatDate(ev.start_date) }}
              </p>
              <div>
                <v-chip v-if="ev.category" x-small :color="ev.category.color || 'grey lighten-2'" class="mr-1">
                  {{ ev.category.name }}
                </v-chip>
                <v-chip x-small :color="temporalColor(ev.temporal_status)" dark>
                  {{ temporalLabel(ev.temporal_status) }}
                </v-chip>
              </div>
            </v-card-text>

            <v-card-actions>
              <v-btn text small color="deep-purple" @click.stop="openDetail(ev)">
                <v-icon left small>mdi-arrow-right</v-icon>
                <span v-if="!$vuetify.breakpoint.xs">Ver detalles</span>
              </v-btn>
              <v-spacer></v-spacer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on" @click.stop="openGoogleCalendar(ev)">
                    <v-icon small color="blue darken-1">mdi-google</v-icon>
                  </v-btn>
                </template>
                <span>Agregar a Google Calendar</span>
              </v-tooltip>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Estado vacío -->
      <v-row v-else justify="center" class="mt-8">
        <v-col cols="12" class="text-center">
          <v-icon size="80" color="grey lighten-1">mdi-calendar-blank</v-icon>
          <p class="text-h6 grey--text mt-3">No hay eventos disponibles</p>
          <p class="caption grey--text">Vuelve pronto para ver nuevos eventos de la región</p>
        </v-col>
      </v-row>

      <!-- Paginación -->
      <v-row v-if="pagination.last_page > 1" justify="center" class="mt-4">
        <v-pagination v-model="pagination.current_page" :length="pagination.last_page"
          @input="cargarEventos" circle color="deep-purple"></v-pagination>
      </v-row>

    </v-container>
  </div>
</template>

<script>
import eventService from '@/services/eventService.js';

export default {
  name: 'EventPublicV',
  data() {
    return {
      eventos: [],
      categorias: [],
      selectedCategory: 'all',
      filterTemporal: 'upcoming',
      filterType: null,
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 12
      },
      typeOptions: [
        { text: 'Todos', value: null },
        { text: 'Presencial', value: 'presencial' },
        { text: 'Virtual', value: 'virtual' },
        { text: 'Híbrido', value: 'hibrido' },
      ],
      temporalOptions: [
        { text: 'Próximos', value: 'upcoming' },
        { text: 'Pasados', value: 'past' },
        { text: 'Todos', value: 'all' },
      ],
      loading: false
    };
  },
  computed: {
    categoriasSelect() {
      const items = [{ text: 'Todos', value: 'all' }];
      this.categorias.forEach(cat => {
        items.push({ text: cat.name, value: cat.slug });
      });
      return items;
    }
  },
  watch: {
    selectedCategory() {
      this.pagination.current_page = 1;
      this.cargarEventos();
    },
    filterTemporal() {
      this.pagination.current_page = 1;
      this.cargarEventos();
    },
    filterType() {
      this.pagination.current_page = 1;
      this.cargarEventos();
    }
  },
  mounted() {
    this.cargarCategorias();
    this.cargarEventos();
  },
  methods: {
    cargarCategorias() {
      eventService.listarCategorias().then(res => {
        this.categorias = res.data;
      });
    },
    cargarEventos() {
      this.loading = true;
      const params = {
        filter: this.filterTemporal,
        page: this.pagination.current_page,
        per_page: this.pagination.per_page
      };
      if (this.selectedCategory && this.selectedCategory !== 'all') {
        params.category = this.selectedCategory;
      }
      if (this.filterType) {
        params.type = this.filterType;
      }

      eventService.listarEventos(params).then(res => {
        this.eventos = res.data.data;
        this.pagination.current_page = res.data.current_page;
        this.pagination.last_page = res.data.last_page;
      }).catch(() => {
        this.eventos = [];
      }).finally(() => {
        this.loading = false;
      });
    },
    getImageUrl(ev) {
      return eventService.getImageUrl(ev.id, ev.image);
    },
    openDetail(ev) {
      this.$router.push('/eventos/' + ev.slug);
    },
    openGoogleCalendar(ev) {
      const formatDt = (dt) => {
        if (!dt) return '';
        return dt.replace(/[-:]/g, '').replace(/\.\d+Z$/, 'Z').replace(/\.\d+$/, '');
      };
      const start = formatDt(ev.start_date);
      const end = ev.end_date ? formatDt(ev.end_date) : start;
      const eventUrl = window.location.origin + '/eventos/' + ev.slug;
      const details = (ev.description || '').substring(0, 300)
        + '\n\nMás info: ' + eventUrl;
      const location = ev.virtual_url || '';
      const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: ev.title,
        dates: start + '/' + end,
        details: details,
        location: location,
      });
      window.open('https://calendar.google.com/calendar/render?' + params.toString(), '_blank');
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      return d.toLocaleDateString('es-PY', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    tipoColor(type) {
      const map = { presencial: 'teal', virtual: 'purple', hibrido: 'indigo' };
      return map[type] || 'grey';
    },
    tipoIcon(type) {
      const map = { presencial: 'mdi-map-marker', virtual: 'mdi-video', hibrido: 'mdi-monitor-cellphone' };
      return map[type] || 'mdi-help';
    },
    tipoLabel(type) {
      const map = { presencial: 'Presencial', virtual: 'Virtual', hibrido: 'Híbrido' };
      return map[type] || type;
    },
    temporalColor(ts) {
      const map = { upcoming: 'green', ongoing: 'blue', past: 'grey' };
      return map[ts] || 'grey';
    },
    temporalLabel(ts) {
      const map = { upcoming: 'Próximo', ongoing: 'En curso', past: 'Finalizado' };
      return map[ts] || ts;
    }
  }
};
</script>

<style scoped>
.event-card {
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
}
.event-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
}
</style>
